import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { makeRequest } from "../../utils/request";
import { User, UserOrganization } from "./userSlice";
import { Organization } from "./organizationSlice";

type ResetPasswordPayload = {
  email: string;
};

type SignInPayload = {
  email?: string;
  password?: string;
  inviteId?: string;
};

type SignUpPayload = {
  email?: string;
  password?: string;
};

const CURRENT_ORG_STORAGE_KEY = "currentOrganizationId";
// Legacy key retained for one-time read so existing admin overrides aren't lost.
const LEGACY_SELECTED_ORG_STORAGE_KEY = "selectedOrganizationId";

const readPersistedCurrentOrganizationId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const current = localStorage.getItem(CURRENT_ORG_STORAGE_KEY);
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_SELECTED_ORG_STORAGE_KEY);
  if (legacy) {
    localStorage.setItem(CURRENT_ORG_STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_SELECTED_ORG_STORAGE_KEY);
    return legacy;
  }
  return undefined;
};

const persistCurrentOrganizationId = (id: string | undefined) => {
  if (typeof window === "undefined") return;
  if (id) localStorage.setItem(CURRENT_ORG_STORAGE_KEY, id);
  else localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
};

export type SessionState = {
  user: User | null;
  /** Hydrated Organization entity for the org the client is currently operating in. */
  currentOrganization: Organization | null;
  /** The org id the client is currently operating in. Sent as `X-Org-Id` on every request. */
  currentOrganizationId?: string;
  error: string | null;
  isLoading: boolean;
  isLoadingSession: boolean;
  languageCode?: string;
  invite?: {
    id: string;
    email?: string;
  };
};

const initialState: SessionState = {
  user: null,
  currentOrganization: null,
  currentOrganizationId: readPersistedCurrentOrganizationId(),
  error: null,
  isLoading: false,
  isLoadingSession: true,
  languageCode: "",
};

const getSession = createAsyncThunk("session/getSession", async (_, { getState }) => {
  // `/v1/auth/session` is purely an auth-state check — it returns the user but
  // not their memberships. Memberships come from `/v1/me/organizations`, which
  // the switcher needs to render. Fan out in parallel on cold boot.
  const [sessionResponse, memberships] = await Promise.all([
    makeRequest<{ data: User }>("/v1/auth/session"),
    makeRequest<{ data: UserOrganization[] }>("/v1/me/organizations").catch(() => ({ data: [] as UserOrganization[] })),
  ]);
  const user: User = { ...(sessionResponse.data || ({} as User)), organizations: memberships.data ?? [] };

  let currentOrganization: Organization | null = null;
  let currentOrganizationId: string | undefined;

  if (user?.emailVerified !== false) {
    const state = getState() as { session: SessionState };
    const persisted = state.session.currentOrganizationId;

    // Prefer the client's persisted selection; otherwise fall back to the user's default.
    // If the persisted org isn't one of the user's memberships and the user isn't an admin,
    // drop it — their last selection is no longer valid.
    const userMemberships = user?.organizations ?? [];
    const persistedIsValid = persisted && (user?.isAdmin || userMemberships.some((m) => m.id === persisted));

    currentOrganizationId = persistedIsValid ? persisted : user?.defaultOrganizationId;

    if (currentOrganizationId) {
      try {
        const orgResponse = await makeRequest<{ data: Organization }>(`/v1/organizations/${currentOrganizationId}`);
        currentOrganization = orgResponse.data;
      } catch {
        // Current selection no longer accessible — fall back to default if different.
        if (currentOrganizationId !== user?.defaultOrganizationId && user?.defaultOrganizationId) {
          currentOrganizationId = user.defaultOrganizationId;
          try {
            const orgResponse = await makeRequest<{ data: Organization }>(`/v1/organizations/${currentOrganizationId}`);
            currentOrganization = orgResponse.data;
          } catch {
            currentOrganization = null;
            currentOrganizationId = undefined;
          }
        } else {
          currentOrganization = null;
          currentOrganizationId = undefined;
        }
      }
    }
  }

  return { user, currentOrganization, currentOrganizationId };
});

const signIn = createAsyncThunk("session/signin", async (payload: SignInPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signin", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signUp = createAsyncThunk("session/signup", async (payload: SignUpPayload, { dispatch }) => {
  await makeRequest("/v1/auth/signup", {
    method: "POST",
    body: payload,
  });

  await dispatch(getSession());
});

const signOut = createAsyncThunk("session/signout", async () => {
  await makeRequest("/v1/auth/signout", {
    method: "POST",
  });
});

const resetPassword = createAsyncThunk("session/resetPassword", async (payload: ResetPasswordPayload) => {
  await makeRequest("/v1/auth/reset", {
    method: "POST",
    body: payload,
  });
});

/**
 * Update the authenticated user's *default* organization server-side.
 * This does not change the in-flight `currentOrganizationId` — switching orgs
 * in the app is a local action; pinning a new default is a server write.
 */
const setDefaultOrganization = createAsyncThunk("session/setDefaultOrganization", async (organizationId: string, { dispatch }) => {
  await makeRequest("/v1/me", {
    method: "PATCH",
    body: { defaultOrganizationId: organizationId },
  });
  await dispatch(getSession());
});

const createOrganization = createAsyncThunk("session/createOrganization", async (name: string, { dispatch }) => {
  // Server sets the new org as the user's default only when it's their first
  // membership. If they already belong somewhere, their default is untouched
  // and we keep the client pointed at their active org — `getSession` will
  // re-validate the persisted selection against the refreshed memberships.
  await makeRequest("/v1/me/organizations", {
    method: "POST",
    body: { name },
  });
  await dispatch(getSession());
});

const leaveOrganization = createAsyncThunk("session/leaveOrganization", async (organizationId: string, { dispatch }) => {
  await makeRequest(`/v1/organizations/${organizationId}/membership`, {
    method: "DELETE",
  });
  persistCurrentOrganizationId(undefined);
  await dispatch(getSession());
});

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setInvite: (state, action) => {
      state.invite = action.payload;
    },
    /**
     * Purely local switch. Updates the selected org id, persists to localStorage,
     * and stashes the org entity if provided. Does NOT touch the server — the
     * CLI and app can each be pointed at different orgs.
     */
    setCurrentOrganization: (state, action: PayloadAction<{ id: string; organization?: Organization | null } | null>) => {
      if (action.payload) {
        state.currentOrganizationId = action.payload.id;
        state.currentOrganization = action.payload.organization ?? state.currentOrganization;
        persistCurrentOrganizationId(action.payload.id);
      } else {
        state.currentOrganizationId = undefined;
        state.currentOrganization = null;
        persistCurrentOrganizationId(undefined);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSession.fulfilled, (state, action) => {
      state.isLoadingSession = false;
      state.user = action.payload.user;
      state.currentOrganization = action.payload.currentOrganization;
      state.currentOrganizationId = action.payload.currentOrganizationId;
      persistCurrentOrganizationId(action.payload.currentOrganizationId);
    });

    builder.addCase(getSession.rejected, (state) => {
      state.isLoadingSession = false;
      state.user = null;
      state.currentOrganization = null;
    });

    builder.addCase(signIn.rejected, (state) => {
      state.error = "Invalid email or password";
    });

    builder.addCase(signOut.fulfilled, (state) => {
      state.isLoading = false;
      state.user = null;
      state.currentOrganization = null;
      state.currentOrganizationId = undefined;
      persistCurrentOrganizationId(undefined);
    });

    builder.addCase(signUp.rejected, (state, action) => {
      state.error = action.error.message || null;
    });

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/pending"),
      (state) => {
        state.isLoading = true;
        state.error = null;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/rejected"),
      (state) => {
        state.isLoading = false;
      },
    );

    builder.addMatcher(
      (action) => action.type.startsWith("session/") && action.type.endsWith("/fulfilled"),
      (state) => {
        state.isLoading = false;
      },
    );
  },
});

export const sessionReducer = sessionSlice.reducer;

export const sessionActions = {
  ...sessionSlice.actions,
  getSession,
  resetPassword,
  signIn,
  signUp,
  signOut,
  setDefaultOrganization,
  createOrganization,
  leaveOrganization,
};
